import { Button } from "@loot-os/ui/components/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@loot-os/ui/components/tooltip";
import { cn } from "@loot-os/ui/lib/utils";
import { Link } from "@tanstack/react-router";
import { Check, Copy, ListFilter } from "lucide-react";
import { type ReactNode, useState } from "react";

import {
  type EventLogRow,
  type EventLogSearch,
  eventPropertyEntries,
  formatEventDelay,
} from "../lib/analyticsEventLog";
import { formatPreciseDateTime } from "../lib/format";

/**
 * Every recorded field of one event, under the row it belongs to: grouped by what the field is
 * about, the properties decoded one per line, and on each id the copy and the filter it offers.
 * Two columns of two groups, paired so their heights come out close: a browser event is long on
 * who and short on properties, a server event the reverse.
 */
export function AnalyticsEventDetail({
  tenantId,
  row,
  onFilter,
}: {
  tenantId: string;
  row: EventLogRow;
  onFilter: (patch: Partial<EventLogSearch>) => void;
}) {
  const entries = eventPropertyEntries(row.properties);
  const delay = formatEventDelay(row.occurredAt, row.receivedAt);
  const { endUserId, anonymousId, sessionId } = row;
  return (
    // Its own container: the columns follow the width the panel is given, not the window's.
    <div className="@container">
      <div className="grid gap-x-12 gap-y-6 py-3 @3xl:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-6">
          <Group title="Event">
            <Field label="Name" mono>
              {row.eventName}
            </Field>
            <Field label="Source">{row.source}</Field>
            <Field label="Event id" mono copy={row.id}>
              {row.id}
            </Field>
            <Field label="Occurred">
              <time dateTime={row.occurredAt} title={row.occurredAt}>
                {formatPreciseDateTime(row.occurredAt)}
              </time>
            </Field>
            <Field label="Received">
              <time dateTime={row.receivedAt} title={row.receivedAt}>
                {formatPreciseDateTime(row.receivedAt)}
              </time>
              {delay && <span className="ml-2 text-muted-foreground tabular-nums">{delay}</span>}
            </Field>
            <Field label="Partition date" mono>
              {row.dt}
            </Field>
          </Group>

          <Group title="Context">
            <Field label="Route" mono>
              {row.route}
            </Field>
            {/* The dimensions the event was stamped with; empty reads "Unknown" in a breakdown. */}
            <Field label="Operating system" mono>
              {row.os}
            </Field>
            <Field label="Device" mono>
              {row.device}
            </Field>
            <Field label="Country" mono>
              {row.country}
            </Field>
            <Field label="Customer segment" mono>
              {row.segment}
            </Field>
            <Field label="Environment" mono copy={row.environmentId}>
              {row.environmentId}
            </Field>
            <Field label="Tenant" mono copy={row.tenantId}>
              {row.tenantId}
            </Field>
          </Group>
        </div>
        <div className="flex min-w-0 flex-col gap-6">
          <Group title="Properties">
            {entries === null ? (
              // Stored text that is not a JSON object: shown as it is rather than guessed at.
              <pre className="max-h-48 overflow-auto font-mono text-xs leading-5 whitespace-pre-wrap">
                {row.properties}
              </pre>
            ) : entries.length === 0 ? (
              <p className="py-0.5 text-sm leading-5 text-muted-foreground">
                This event carries no properties.
              </p>
            ) : (
              entries.map((entry) => (
                <Field
                  key={entry.key}
                  label={entry.key}
                  labelMono
                  mono
                  copy={entry.isText ? entry.value : null}
                >
                  {entry.key === "boxId" && entry.isText ? (
                    <Link
                      to="/tenants/$tenantId/boxes/$boxId"
                      params={{ tenantId, boxId: entry.value }}
                      className="text-primary hover:underline"
                    >
                      {entry.value}
                    </Link>
                  ) : (
                    entry.value
                  )}
                </Field>
              ))
            )}
          </Group>

          <Group title="Who">
            <Field
              label="Shopper id"
              mono
              copy={endUserId}
              filter={
                endUserId
                  ? { label: "Only this shopper", onSelect: () => onFilter({ user: endUserId }) }
                  : null
              }
            >
              {endUserId && (
                <Link
                  to="/tenants/$tenantId/users/$userId"
                  params={{ tenantId, userId: endUserId }}
                  className="text-primary hover:underline"
                >
                  {endUserId}
                </Link>
              )}
            </Field>
            <Field label="Username">{row.endUser?.username}</Field>
            <Field label="External id" mono copy={row.endUser?.externalId}>
              {row.endUser?.externalId}
            </Field>
            <Field
              label="Browser id"
              mono
              copy={anonymousId}
              filter={
                anonymousId
                  ? { label: "Only this browser", onSelect: () => onFilter({ anon: anonymousId }) }
                  : null
              }
            >
              {anonymousId}
            </Field>
            <Field
              label="Tab id"
              mono
              copy={sessionId}
              filter={
                sessionId
                  ? { label: "Only this tab", onSelect: () => onFilter({ session: sessionId }) }
                  : null
              }
            >
              {sessionId}
            </Field>
            <Field label="User agent">
              {row.userAgent && <span className="text-xs">{row.userAgent}</span>}
            </Field>
          </Group>
        </div>
      </div>
    </div>
  );
}

/**
 * A heading over the fields that share a subject. Bold and in the foreground color, the one
 * bright line in a block of muted labels, so the four groups are found before any field is read.
 */
function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="min-w-0">
      <h3 className="mb-2 text-xs font-bold tracking-wider text-foreground uppercase">{title}</h3>
      <dl className="grid grid-cols-[minmax(7rem,max-content)_minmax(0,1fr)] gap-x-4 text-sm">
        {children}
      </dl>
    </section>
  );
}

/** What a field's filter button says and does. */
interface FieldFilter {
  label: string;
  onSelect: () => void;
}

/**
 * One label and its value, a dash when the event recorded none. Every line is as tall as the
 * icon buttons, so a field with actions and one without keep the same rhythm.
 */
function Field({
  label,
  labelMono,
  mono,
  copy,
  filter,
  children,
}: {
  label: string;
  /** The label is itself a recorded key (a property name), printed as one. */
  labelMono?: boolean;
  mono?: boolean;
  /** What the copy button puts on the clipboard; no button without it. */
  copy?: string | null;
  filter?: FieldFilter | null;
  children: ReactNode;
}) {
  const empty =
    children === null || children === undefined || children === false || children === "";
  return (
    <>
      <dt
        className={cn(
          "py-0.5 leading-5 text-muted-foreground",
          labelMono && "font-mono text-xs leading-5",
        )}
      >
        {label}
      </dt>
      <dd className="flex min-w-0 items-start gap-1">
        <span
          className={cn(
            "min-w-0 py-0.5 leading-5 break-words",
            mono && "font-mono text-xs leading-5",
          )}
        >
          {empty ? <span className="text-muted-foreground">—</span> : children}
        </span>
        {!empty && (copy || filter) && (
          <span className="flex shrink-0">
            {copy && (
              <CopyAction text={copy} label={`Copy ${labelMono ? label : lowerFirst(label)}`} />
            )}
            {filter && (
              <IconAction label={filter.label} onClick={filter.onSelect}>
                <ListFilter />
              </IconAction>
            )}
          </span>
        )}
      </dd>
    </>
  );
}

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

/** A quiet icon button that names itself on hover and to a screen reader. */
function IconAction({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={label}
            className="text-muted-foreground hover:text-foreground"
            onClick={onClick}
          />
        }
      >
        {children}
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

function CopyAction({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <IconAction label={copied ? "Copied" : label} onClick={() => void copy()}>
      {copied ? <Check /> : <Copy />}
    </IconAction>
  );
}
